import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import izenuAPI from '../../services/izenuAPI';
import openArtistInApp from './openArtistInApp';
import CampaignFollowupPanel, { FOLLOWUP_STAGES, stageLabel } from './CampaignFollowupPanel';

// Marketing → Pré-inscrições: leads from app.izenu.ao/100mil.
// Each lead is matched to an account (by user_id, email or phone) and to the
// EPs/albums it already has live (streaming or on sale), so the follow-up starts from the facts.

const SIZE_LABEL = { ep_2: 'EP · 2 faixas', ep_3_6: 'EP · 3–6 faixas', album_7: 'Álbum · 7+' };
const READY_LABEL = { ready: 'Pronto', in_production: 'Em produção' };
const STATUS = [
  { key: 'new', label: 'Novo', color: '#1565C0', bg: '#E3F2FD' },
  { key: 'contacted', label: 'Contactado', color: '#6C3FC5', bg: '#EFE9FA' },
  { key: 'registered', label: 'Criou conta', color: '#00838F', bg: '#E0F7FA' },
  { key: 'published', label: 'Publicado', color: '#2E7D32', bg: '#E8F5E9' },
  { key: 'paid', label: 'Incentivo pago', color: '#8D6E00', bg: '#FFF8E1' },
  { key: 'done', label: 'Concluído', color: '#FFFFFF', bg: '#2E7D32' },
  { key: 'discarded', label: 'Descartado', color: '#757575', bg: '#F5F5F5' },
];
const ACCOUNT_FILTERS = [
  { key: '', label: 'Todas as contas' },
  { key: 'none', label: 'Sem conta Izenu' },
  { key: 'uploaded', label: 'Já subiu EP/álbum' },
  { key: 'not_uploaded', label: 'Tem conta, não subiu EP/álbum' },
];
const LIVE_FILTERS = [
  { key: '', label: 'Publicado ou não' },
  { key: 'yes', label: 'EP/álbum publicado' },
  { key: 'no', label: 'EP/álbum ainda não publicado' },
];
const FOLLOWUP_FILTERS = [
  { key: '', label: 'Emails automáticos: todos' },
  ...FOLLOWUP_STAGES.map((s) => ({ key: s.key, label: `Fase: ${s.label}` })),
  { key: 'none', label: 'Fora das fases (publicou ou concluído)' },
  { key: 'optout', label: 'Não recebe emails' },
];
const PER_PAGE = 50;
const EMPTY_STATS = { total: 0, account: 0, live: 0, uploaded: 0, potential: 0, due: 0, dueCount: 0, review: 0, reviewValue: 0 };
const kz = (n) => `${Number(n || 0).toLocaleString('pt-PT')} Kz`;
const statusOf = (k) => STATUS.find((s) => s.key === k) || STATUS[0];

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T'));
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' }) + ' ' +
    d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
};

const waLink = (phone, artist) => {
  const text = `Olá ${artist}! Aqui é a equipa da Izenu, sobre a tua pré-inscrição na campanha 100.000 Kz.`;
  return `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;
};

function exportCsv(rows) {
  const cols = [
    ['Data', (r) => r.created_at],
    ['Nome artístico', (r) => r.artist_name],
    ['Nome', (r) => r.contact_name],
    ['Telefone', (r) => r.phone],
    ['Email', (r) => r.email || ''],
    ['Lançamento', (r) => SIZE_LABEL[r.release_size]],
    ['Conta Izenu', (r) => r.account_id || ''],
    ['Tipo de conta', (r) => r.account_type || ''],
    ['EP/álbuns carregados', (r) => r.releases_uploaded ?? ''],
    ['EP/álbuns publicados', (r) => r.releases_live ?? ''],
    ['Incentivo a pagar (Kz)', (r) => r.incentive_due ?? 0],
    ['Regulamento (versão)', (r) => r.terms_version || ''],
    ['Regulamento aceite em', (r) => r.terms_accepted_at || ''],
    ['Estado', (r) => statusOf(r.status).label],
    ['Notas', (r) => r.notes || ''],
    ['utm_source', (r) => r.utm_source || ''],
    ['utm_medium', (r) => r.utm_medium || ''],
    ['utm_campaign', (r) => r.utm_campaign || ''],
    ['utm_content', (r) => r.utm_content || ''],
  ];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.map((c) => esc(c[0])).join(';'), ...rows.map((r) => cols.map((c) => esc(c[1](r))).join(';'))].join('\r\n');
  // BOM so Excel opens accents correctly; ";" is what Excel expects in pt locales.
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `pre-inscricoes-100mil-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function Card({ label, value, sub }) {
  return (
    <div style={{ flex: '1 1 160px', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, padding: '14px 16px' }}>
      <div style={{ fontSize: 12, color: '#777' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#222', marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function NotesCell({ lead, onSave }) {
  const [value, setValue] = useState(lead.notes || '');
  useEffect(() => setValue(lead.notes || ''), [lead.notes]);
  return (
    <textarea
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => value !== (lead.notes || '') && onSave(value)}
      placeholder="Notas…"
      rows={2}
      style={{ width: 180, fontSize: 12, padding: 6, border: '1px solid #ddd', borderRadius: 4, resize: 'vertical' }}
    />
  );
}

export default function CampaignLeads() {
  const [tab, setTab] = useState(() => (window.location.hash === '#emails' ? 'emails' : 'leads'));
  const [followup, setFollowup] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [installed, setInstalled] = useState(true);
  const [status, setStatus] = useState('');
  const [account, setAccount] = useState('');
  const [live, setLive] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const filters = { campaign: '100mil', status, account, live, followup, q };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await izenuAPI.get('/admin/campaign/leads.php', {
        params: { ...filters, page, per_page: PER_PAGE },
      });
      setInstalled(r.data.installed !== false);
      setLeads(r.data.data || []);
      setTotal(r.data.total ?? (r.data.data || []).length);
      setStats(r.data.stats || EMPTY_STATS);
    } catch (e) {
      setError('Não foi possível carregar as pré-inscrições.');
    } finally {
      setLoading(false);
    }
  }, [status, account, live, followup, q, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // A new filter starts from the first page.
  useEffect(() => setPage(1), [status, account, live, followup, q]);

  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  // The CSV has every filtered lead, not just the page on screen.
  const exportAll = async () => {
    setExporting(true);
    try {
      const r = await izenuAPI.get('/admin/campaign/leads.php', { params: { ...filters, per_page: 0 } });
      exportCsv(r.data.data || []);
    } catch (e) {
      setError('Não foi possível exportar.');
    } finally {
      setExporting(false);
    }
  };

  const setOptout = async (lead, optout) => {
    setLeads((list) => list.map((l) => (l.followup_email === lead.followup_email ? { ...l, followup_optout: optout } : l)));
    try {
      await izenuAPI.post('/admin/campaign/followup.php', { action: 'optout', email: lead.followup_email, optout });
    } catch (e) {
      setError(e.response?.data?.message || 'Não foi possível guardar. Recarrega a página.');
    }
  };

  const showTab = (t) => {
    setTab(t);
    window.history.replaceState(null, '', t === 'emails' ? '#emails' : window.location.pathname);
  };

  const update = async (id, patch) => {
    setLeads((list) => list.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    try {
      await izenuAPI.post('/admin/campaign/leads.php', { id, ...patch });
    } catch (e) {
      setError('Não foi possível guardar. Recarrega a página.');
    }
  };


  const th = { textAlign: 'left', padding: '10px 8px', fontSize: 12, color: '#666', fontWeight: 600, borderBottom: '1px solid #e5e5e5', whiteSpace: 'nowrap' };
  const td = { padding: '10px 8px', fontSize: 13, color: '#333', borderBottom: '1px solid #f0f0f0', verticalAlign: 'top' };

  return (
    <div style={{ padding: 40 }}>
      <h2 style={{ color: '#333', marginBottom: 6, fontSize: 22, fontWeight: 700 }}>Pré-inscrições — Campanha 100.000 Kz</h2>
      <p style={{ color: '#666', marginBottom: 24, fontSize: 14 }}>
        Artistas que preencheram o formulário em{' '}
        <a href="https://app.izenu.ao/100mil" target="_blank" rel="noreferrer" style={{ color: '#6C3FC5' }}>app.izenu.ao/100mil</a>.
        A coluna “Conta” é encontrada pelo email ou telefone.
      </p>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e5e5', marginBottom: 24 }}>
        {[['leads', 'Pré-inscrições'], ['emails', 'Emails automáticos']].map(([k, label]) => (
          <button
            key={k}
            onClick={() => showTab(k)}
            style={{
              padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              color: tab === k ? '#6C3FC5' : '#777', borderBottom: tab === k ? '2px solid #6C3FC5' : '2px solid transparent', marginBottom: -1,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'emails' ? <CampaignFollowupPanel /> : (<>

      {!installed && (
        <div style={{ padding: 16, background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>
          A tabela ainda não existe — correr <code>php bin/migration_campaign_leads.php</code> no servidor.
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <Card label="Pré-inscrições" value={stats.total} />
        <Card label="Com conta Izenu" value={stats.account} />
        <Card label="Já subiu EP/álbum" value={stats.uploaded} sub="por conta própria" />
        <Card label="Já com EP/álbum publicado" value={stats.live} />
        <Card label="Incentivo potencial" value={kz(stats.potential)} sub="sem os descartados" />
        <Card
          label="Incentivo a pagar"
          value={kz(stats.due)}
          sub={`${stats.dueCount} aprovado(s) por pagar · ${stats.review} em revisão (${kz(stats.reviewValue)})`}
        />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Procurar nome, telefone, email…"
          style={{ padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14, minWidth: 260 }}
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 }}
        >
          <option value="">Todos os estados</option>
          <option value="not_done">Não concluídos</option>
          {STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select
          value={account}
          onChange={(e) => setAccount(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 }}
        >
          {ACCOUNT_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <select
          value={live}
          onChange={(e) => setLive(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 }}
        >
          {LIVE_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <select
          value={followup}
          onChange={(e) => setFollowup(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 }}
        >
          {FOLLOWUP_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <button
          onClick={exportAll}
          disabled={!total || exporting}
          style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: 6, border: 'none', background: '#6C3FC5', color: '#fff', fontWeight: 600, fontSize: 14, cursor: total && !exporting ? 'pointer' : 'default', opacity: total && !exporting ? 1 : 0.5 }}
        >
          {exporting ? 'A exportar…' : `Exportar CSV (${total})`}
        </button>
      </div>

      {error && <p style={{ color: '#C62828', fontSize: 14 }}>{error}</p>}

      <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Data</th>
              <th style={th}>Artista</th>
              <th style={th}>Contacto</th>
              <th style={th}>Lançamento</th>
              <th style={th}>Origem</th>
              <th style={th}>Conta</th>
              <th style={th}>Emails auto.</th>
              <th style={th}>Estado</th>
              <th style={th}>Notas</th>
            </tr>
          </thead>
          <tbody>
            {loading && !leads.length ? (
              <tr><td style={td} colSpan={9}>A carregar…</td></tr>
            ) : !leads.length ? (
              <tr><td style={{ ...td, color: '#999' }} colSpan={9}>{q || status || account || live || followup ? 'Nenhuma pré-inscrição com estes filtros.' : 'Ainda não há pré-inscrições.'}</td></tr>
            ) : leads.map((l) => {
              const st = statusOf(l.status);
              return (
                <tr key={l.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: '#777' }}>{fmtDate(l.created_at)}</td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{l.artist_name}</div>
                    <div style={{ color: '#777', fontSize: 12 }}>{l.contact_name}</div>
                    {l.genre && <div style={{ color: '#999', fontSize: 12 }}>{l.genre}</div>}
                    {l.social_url && (
                      <a href={l.social_url} target="_blank" rel="noreferrer" style={{ color: '#6C3FC5', fontSize: 12, wordBreak: 'break-all' }}>
                        {l.social_url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 40)}
                      </a>
                    )}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <a href={waLink(l.phone, l.artist_name)} target="_blank" rel="noreferrer" style={{ color: '#2E7D32', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {l.phone} ↗
                      </a>
                      {/* impersonate.php only accepts acc_type = 'artist' */}
                      {l.account_type === 'artist' && (
                        <button
                          onClick={() => openArtistInApp({ user_id: l.account_id, artist_name: l.artist_name })}
                          title="Abrir no app.izenu.ao como artista"
                          aria-label="Abrir no app.izenu.ao como artista"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', border: '1px solid #6C3FC5', borderRadius: 12, background: 'none', cursor: 'pointer', color: '#6C3FC5', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Ver como artista
                        </button>
                      )}
                      {l.songs_count > 0 && (
                        <Link
                          to={`/list-musics?user_id=${l.account_id}&artist=${encodeURIComponent(l.artist_name || l.name || '')}`}
                          title="Músicas desta conta em Catálogo → Músicas"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', border: '1px solid #6C3FC5', borderRadius: 12, color: '#6C3FC5', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', textDecoration: 'none' }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                          </svg>
                          Ver músicas ({l.songs_count})
                        </Link>
                      )}
                    </div>
                    {l.email && <div><a href={`mailto:${l.email}`} style={{ color: '#555', fontSize: 12 }}>{l.email}</a></div>}
                    <div style={{ fontSize: 11, color: l.terms_version ? '#2E7D32' : '#B26A00', marginTop: 4 }} title={l.terms_accepted_at || ''}>
                      {l.terms_version ? `✓ Regulamento ${l.terms_version}` : 'Regulamento não aceite'}
                    </div>
                  </td>
                  <td style={td}>
                    <div style={{ whiteSpace: 'nowrap' }}>{SIZE_LABEL[l.release_size]}</div>
                    {l.release_ready && <div style={{ fontSize: 12, color: l.release_ready === 'ready' ? '#2E7D32' : '#B26A00' }}>{READY_LABEL[l.release_ready]}</div>}
                  </td>
                  <td style={{ ...td, fontSize: 12, color: '#777' }}>
                    {l.utm_source || '—'}
                    {l.utm_medium && <div>{l.utm_medium}</div>}
                    {l.utm_content && <div>{l.utm_content}</div>}
                  </td>
                  <td style={{ ...td, fontSize: 12 }}>
                    {l.account_id ? (
                      <>
                        <div>#{l.account_id} · {l.account_type || 'ouvinte'}</div>
                        <div style={{ color: Number(l.releases_live) > 0 ? '#2E7D32' : '#999', fontWeight: Number(l.releases_live) > 0 ? 600 : 400 }}>
                          {Number(l.releases_live)} EP/álbum publicado
                        </div>
                        {Number(l.releases_uploaded) > Number(l.releases_live) && (
                          <div style={{ color: '#6C3FC5' }}>{Number(l.releases_uploaded)} EP/álbum carregado(s)</div>
                        )}
                        {(l.incentive_releases || []).map((r) => (
                          <div key={r.release_id} style={{ fontSize: 12, marginTop: 4 }} title={`${r.approved}/${r.tracks} faixas aprovadas`}>
                            <span style={{ color: '#555' }}>{r.title}</span>{' · '}
                            <span style={{ color: '#999' }}>{r.mode === 'sale' ? 'venda' : 'streaming'}</span>{' · '}
                            {r.paid ? (
                              <span style={{ color: '#8D6E00' }}>pago</span>
                            ) : r.eligible ? (
                              <strong style={{ color: '#2E7D32' }}>a pagar {kz(r.amount)}</strong>
                            ) : r.tracks < 2 ? (
                              <span style={{ color: '#999' }}>só {r.tracks} faixa</span>
                            ) : r.approved === r.tracks && !r.live ? (
                              <span style={{ color: '#B26A00' }} title="Não está à venda e tem faixas ocultas do público">faixas ocultas</span>
                            ) : (
                              <span style={{ color: '#B26A00' }}>em revisão {r.approved}/{r.tracks}</span>
                            )}
                          </div>
                        ))}
                      </>
                    ) : <span style={{ color: '#999' }}>Sem conta</span>}
                  </td>
                  <td style={{ ...td, fontSize: 12, whiteSpace: 'nowrap' }}>
                    {l.followup_stage
                      ? <div style={{ fontWeight: 600, color: '#6C3FC5' }}>{stageLabel(l.followup_stage)}</div>
                      : <div style={{ color: '#999' }}>Fora das fases</div>}
                    {l.followup_last ? (
                      <div style={{ color: l.followup_last.error ? '#C62828' : '#555' }} title={l.followup_last.error || ''}>
                        {l.followup_last.error ? 'Falhou' : 'Último'}: {stageLabel(l.followup_last.stage)} {l.followup_last.step}.º · {fmtDate(l.followup_last.sent_at)}
                      </div>
                    ) : l.followup_stage && <div style={{ color: '#999' }}>Nenhum enviado</div>}
                    {!l.followup_email ? (
                      <div style={{ color: '#B26A00' }}>Sem email</div>
                    ) : l.followup_optout ? (
                      <button onClick={() => setOptout(l, false)} title={`Não recebe emails em ${l.followup_email}`} style={linkBtn('#C62828')}>
                        Não recebe · retomar
                      </button>
                    ) : (
                      <button onClick={() => setOptout(l, true)} title={`Deixar de enviar para ${l.followup_email}`} style={linkBtn('#777')}>
                        Não enviar
                      </button>
                    )}
                  </td>
                  <td style={td}>
                    <select
                      value={l.status}
                      onChange={(e) => update(l.id, { status: e.target.value })}
                      style={{ padding: '4px 8px', borderRadius: 12, border: `1px solid ${st.color}`, background: st.bg, color: st.color, fontWeight: 600, fontSize: 12 }}
                    >
                      {STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </td>
                  <td style={td}>
                    <NotesCell lead={l} onSave={(notes) => update(l.id, { notes })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, fontSize: 13, color: '#666' }}>
          <span>
            {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} de {total}
            {loading && ' · a carregar…'}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setPage((p) => p - 1)} disabled={page <= 1 || loading} style={pagerBtn(page <= 1 || loading)}>‹ Anterior</button>
            <span>Página {page} de {pages}</span>
            <button onClick={() => setPage((p) => p + 1)} disabled={page >= pages || loading} style={pagerBtn(page >= pages || loading)}>Seguinte ›</button>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}

const linkBtn = (color) => ({
  marginTop: 4, padding: 0, border: 'none', background: 'none', color, fontSize: 12, textDecoration: 'underline', cursor: 'pointer',
});

const pagerBtn = (disabled) => ({
  padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', fontSize: 13,
  color: disabled ? '#bbb' : '#333', cursor: disabled ? 'default' : 'pointer',
});
